import type { NativeHomeSection } from "../storage/homeConfig";

type Input = {
  sections: NativeHomeSection[];
  discoveryKeys: string[];
  becauseYouWatchedKeys: string[];
  libraryKeys: string[];
  recommendedMovieKey?: string;
  recommendedShowKey?: string;
  primaryReady: boolean;
  moviesEnabled: boolean;
  showsEnabled: boolean;
};

export function interleavePublicDiscoveryRows<T>(contextual: T[], generic: T[], contextualStride = 2) {
  if (!generic.length) return contextual;
  const rows: T[] = [];
  let contextualIndex = 0;
  let genericIndex = 0;
  const stride = Math.max(1, Math.floor(contextualStride));
  while (contextualIndex < contextual.length || genericIndex < generic.length) {
    for (let count = 0; count < stride && contextualIndex < contextual.length; count += 1) {
      rows.push(contextual[contextualIndex++]);
    }
    if (genericIndex < generic.length) rows.push(generic[genericIndex++]);
  }
  return rows;
}

function sectionKeys(
  section: NativeHomeSection,
  input: Pick<Input, "discoveryKeys" | "becauseYouWatchedKeys" | "libraryKeys" | "recommendedMovieKey" | "recommendedShowKey">,
) {
  if (!section.enabled) return [];
  if (section.type === "personalizedRecommendations") return input.discoveryKeys;
  if (section.type === "becauseYouWatched") return input.becauseYouWatchedKeys;
  if (section.type === "serverLibraries") return input.libraryKeys;
  if (section.type === "recommended") return [input.recommendedMovieKey, input.recommendedShowKey].filter((key): key is string => Boolean(key));
  if (section.type === "continueWatchingNextUp") return ["continue-combined"];
  if (["continueWatching", "resume"].includes(section.type)) return ["continue-watching"];
  if (section.type === "nextUp") return ["next-up"];
  if (section.type === "genres") return ["genres"];
  if (section.type === "streamingServices") return ["streaming-services"];
  if (section.type === "watchItAgain") return ["watch-again"];
  if (section.type === "recentlyAdded") {
    const title = section.title.toLowerCase();
    if (title.includes("series") || title.includes("show")) return ["recent-series"];
    if (title.includes("movie")) return ["recent-movies"];
    return ["recent-movies", "recent-series"];
  }
  if (section.type === "recommendedMovies") return input.recommendedMovieKey ? [input.recommendedMovieKey] : [];
  if (section.type === "recommendedShows") return input.recommendedShowKey ? [input.recommendedShowKey] : [];
  if (section.title === "Top Rated Movies") return ["top-movies"];
  if (section.title === "Top Rated TV Shows") return ["top-series"];
  return [];
}

/** Preserve the user's configured outer order while expanding the one dynamic slot. */
export function composeHomeRowKeys(input: Input) {
  const configured = input.sections.flatMap((section) => sectionKeys(section, input));
  const watchKeys = ["continue-combined", "continue-watching", "next-up"];
  const primaryWatchKey = configured.find((key) => watchKeys.includes(key)) ?? "continue-combined";
  const assembled = input.primaryReady
    ? [primaryWatchKey, ...configured.filter((key) => key !== primaryWatchKey)]
    : [primaryWatchKey];
  const seen = new Set<string>();
  return assembled.filter((key) => {
    if ((!input.moviesEnabled && ["top-movies", "recent-movies", input.recommendedMovieKey].includes(key))
      || (!input.showsEnabled && ["top-series", "recent-series", input.recommendedShowKey].includes(key))
      || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

type RecommendationRow = {
  key: string;
  items?: Array<{ Id?: string }>;
  recommendation?: {
    recommendationType?: string;
    mediaKind?: "Movie" | "Series";
  };
};

/** Do not repeat an explicit Recently Added shelf in the dynamic feed. */
export function removeRedundantHomeRows<T extends RecommendationRow>(rows: T[]) {
  const fixedRecentMovies = rows.some((row) => row.key === "recent-movies");
  const fixedRecentShows = rows.some((row) => row.key === "recent-series");
  const withoutRedundantRows = rows.filter((row) => {
    if (row.recommendation?.recommendationType !== "RecentlyAdded") return true;
    return row.recommendation.mediaKind === "Movie"
      ? !fixedRecentMovies
      : row.recommendation.mediaKind === "Series"
        ? !fixedRecentShows
        : true;
  });
  const fixedItemIds = new Set(withoutRedundantRows
    .filter((row) => !row.recommendation)
    .flatMap((row) => row.items ?? [])
    .flatMap((item) => item.Id ? [item.Id] : []));
  const recommendationItemIds = new Set<string>();
  return withoutRedundantRows.flatMap((row) => {
    if (!row.recommendation || !row.items) return [row];
    const items = row.items.filter((item) => {
      if (!item.Id || fixedItemIds.has(item.Id) || recommendationItemIds.has(item.Id)) return false;
      recommendationItemIds.add(item.Id);
      return true;
    });
    return items.length >= 4 ? [{ ...row, items }] : [];
  });
}
